// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { Checkbox, Typography } from '@mui/material';
import { AppIcon, Label } from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import {
  NavigationControls,
  ScreenLayout,
} from '@shared/advisory/account-workflow/layout';
import { useIdentityVerification } from './use-identity-verification';

export const IdentityVerification = defineComponent({
  displayName: 'AccountOpeningIdentityVerification',
  useSetup: useIdentityVerification,
  View: ({
    qrCodeSrc,
    qrAlt,
    identityVerificationUrl,
    setVerificationCompleted,
    verificationCompleted,
    onNext,
  }) => (
    <ScreenLayout
      actions={
        <NavigationControls
          onNext={onNext}
          nextLabel={<Translation i18nKey="common.continue" ns="common" />}
          nextDisabled={!verificationCompleted}
        />
      }
      className="justify-center"
    >
      <div className="mx-auto flex w-full max-w-2xl flex-col items-center gap-6 py-8 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-3xl bg-page">
          <AppIcon icon="Smartphone" size="2xl" color="gray" />
        </div>

        <div className="space-y-3">
          <Typography
            variant="h4"
            className="!text-3xl !font-semibold"
            data-testid="identity-verification-title"
          >
            <Translation
              i18nKey="account-opening.screens.identity-verification.title"
              ns="components"
            />
          </Typography>

          <Typography
            variant="body1"
            className="mx-auto max-w-xl !text-base !leading-8 !text-subtitle"
            data-testid="identity-verification-description"
          >
            <Translation
              i18nKey="account-opening.screens.identity-verification.description"
              ns="components"
            />
          </Typography>
        </div>

        {qrCodeSrc ? (
          <div className="rounded-3xl border border-border-medium/70 bg-white p-4 shadow-sm">
            <img
              src={qrCodeSrc}
              alt={qrAlt}
              className="h-60 w-60 rounded-2xl"
              data-testid="identity-verification-qr-code"
            />
          </div>
        ) : null}

        {identityVerificationUrl ? (
          <a
            href={identityVerificationUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-base font-medium text-secondary underline underline-offset-2 hover:text-secondary/75"
            data-testid="identity-verification-direct-link"
          >
            <Translation
              i18nKey="account-opening.screens.identity-verification.direct-link"
              ns="components"
            />
          </a>
        ) : null}

        <div className="mx-auto flex w-full max-w-xl justify-center">
          <div
            className="flex max-w-xl items-start space-x-2"
            data-testid="identity-verification-completed-confirmation"
          >
            <Checkbox
              id="identity-verification-completed-confirmation-checkbox"
              data-testid="identity-verification-completed-confirmation-checkbox"
              checked={verificationCompleted}
              color="primary"
              onChange={() => setVerificationCompleted(!verificationCompleted)}
              className="!mt-1 !p-0"
            />

            <Label
              htmlFor="identity-verification-completed-confirmation-checkbox"
              className="flex flex-1 cursor-pointer !text-sm !leading-relaxed !text-subtitle"
              data-testid="identity-verification-completed-confirmation-label"
            >
              <Translation
                i18nKey="account-opening.screens.identity-verification.completed-confirmation"
                ns="components"
              />
            </Label>
          </div>
        </div>
      </div>
    </ScreenLayout>
  ),
});
